import axios from 'axios';

const BASE_URL = 'https://arndapi.test123.dev';
// const BASE_URL = 'http://localhost:4000';

const api = axios.create({
  baseURL: `${BASE_URL}`,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('a_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Legal Documents API ---

export const getDocument = async (type) => {
  try {
    const response = await api.get(`/admin/documents/${type}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch document: ${type}`
    );
  }
};

export const setDocument = async (doc) => {
  try {
    const response = await api.post(`/admin/documents`, doc);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to create/update document'
    );
  }
};

export const updateDocument = async (type, content) => {
  try {
    const response = await api.put(`/admin/documents/${type}`, content);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to update document: ${type}`
    );
  }
};

// --- Prices API ---

export const getPrices = async () => {
  try {
    const res = await api.get('/admin/prices');
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch prices'
    );
  }
};

export const setPrices = async (payload) => {
  try {
    const res = await api.post('/admin/prices', payload);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to create/update prices'
    );
  }
};

export const updatePrices = async (payload) => {
  try {
    const res = await api.put('/admin/prices', payload);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to update prices'
    );
  }
};

// --- Users API ---

export const getUsers = async (filters = {}) => {
  try {
    const res = await api.get('/admin/users', { params: filters });
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch users'
    );
  }
};

export const getUserById = async (userId) => {
  try {
    const res = await api.get(`/admin/users/${userId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch user: ${userId}`
    );
  }
};

export const updateUser = async (userId, data) => {
  try {
    const res = await api.put(`/admin/users/${userId}`, data);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to update user: ${userId}`
    );
  }
};

export const banUser = async (userId) => {
  try {
    const res = await api.put(`/admin/users/${userId}/ban`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to ban user: ${userId}`
    );
  }
};

export const unbanUser = async (userId) => {
  try {
    const res = await api.put(`/admin/users/${userId}/unban`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to unban user: ${userId}`
    );
  }
};

// --- Events API ---

export const getAllEvents = async (filters = {}) => {
  try {
    const res = await api.get('/admin/events', { params: filters });
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch events'
    );
  }
};

export const getEventById = async (eventId) => {
  try {
    const res = await api.get(`/admin/events/${eventId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch event: ${eventId}`
    );
  }
};

export const createEvent = async (eventData) => {
  try {
    const res = await api.post('/event', eventData);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to create event'
    );
  }
};

export const updateEvent = async (eventId, eventData) => {
  try {
    const res = await api.put(`/admin/events/${eventId}`, eventData);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to update event: ${eventId}`
    );
  }
};

export const deleteEvent = async (eventId) => {
  try {
    const res = await api.delete(`/admin/events/${eventId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to delete event: ${eventId}`
    );
  }
};

export const assignPhotographers = async (eventId, photographerIds) => {
  try {
    const res = await api.put(`/admin/events/${eventId}/assign-photographers`, { photographerIds });
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to assign photographers to event: ${eventId}`
    );
  }
};

// --- Photographers API ---

export const getAllPhotographers = async () => {
  try {
    const res = await api.get('/admin/photographers');
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch photographers'
    );
  }
};

export const updatePhotographer = async (userId, data) => {
  try {
    const res = await api.put(`/admin/photographers/${userId}`, data);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to update photographer: ${userId}`
    );
  }
};

// --- Photos API ---

export const getAllPhotos = async () => {
  try {
    const res = await api.get('/admin/photos');
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch photos'
    );
  }
};

export const getPhotosByUser = async (userId) => {
  try {
    const res = await api.get(`/admin/photos/by-user/${userId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch photos for user: ${userId}`
    );
  }
};

export const getPhotosByEvent = async (eventId) => {
  try {
    const res = await api.get(`/admin/photos/by-event/${eventId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to fetch photos for event: ${eventId}`
    );
  }
};

export const removePhoto = async (photoId) => {
  try {
    const res = await api.delete(`/admin/photos/${photoId}`);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || `Failed to remove photo: ${photoId}`
    );
  }
};

// --- Statistics API ---

export const getStats = async () => {
  try {
    const res = await api.get('/admin/stats');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch stats');
  }
};

export const getSalesTrends = async () => {
  try {
    const res = await api.get('/admin/stats/sales-trends');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch sales trends');
  }
};

export const getUploadsTrends = async () => {
  try {
    const res = await api.get('/admin/stats/uploads-trends');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch uploads trends');
  }
};

export const getUserActivityTrends = async () => {
  try {
    const res = await api.get('/admin/stats/user-activity');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user activity trends');
  }
};

export const getEventAttendanceStats = async () => {
  try {
    const res = await api.get('/admin/stats/event-attendance');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch event attendance stats');
  }
};

export const getPurchasesStats = async () => {
  try {
    const res = await api.get('/admin/stats/purchases');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch purchases stats');
  }
};

// --- S3 Image Upload for Events ---

export const getEventImagePresignedUrls = async (iconKey, bannerKey) => {
  try {
    const res = await api.post('/s3/event/icon-banner-urls', {
      iconKey,
      bannerKey,
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to get presigned URLs');
  }
};

export const uploadImageToS3 = async (presignedUrl, file) => {
  try {
    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type,
        // 'x-amz-acl': 'public-read',
      },
    });
    return true;
  } catch (error) {
    throw new Error('Failed to upload image to S3');
  }
};

// --- Survey API ---
export const getSurveyQuestions = async () => {
  try {
    const res = await api.get('/survey/questions');
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch survey questions');
  }
};

export const setSurvey = async (questions) => {
  try {
    const res = await api.put('/admin/survey', questions);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to set survey questions');
  }
};

// --- Photo Match API ---
export const runPhotoMatch = async ({ eventId, photoId }) => {
  try {
    const res = await api.post('/admin/photos/match', { eventId, photoId });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to run photo match');
  }
};

// --- E2E Test Harness API (SCRUM-272) ---

export const runE2ETest = async (file, eventName) => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    if (eventName) formData.append('eventName', eventName);
    const res = await api.post('/admin/test/run', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to start E2E test');
  }
};

export const getE2ETestStatus = async (eventId) => {
  try {
    const res = await api.get(`/admin/test/status/${eventId}`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to get test status: ${eventId}`);
  }
};

export const cleanupE2ETest = async (eventId) => {
  try {
    const res = await api.post(`/admin/test/cleanup/${eventId}`);
    return res.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Failed to clean up test: ${eventId}`);
  }
};